import { notifyAuthChanged } from '../../utils/authSession';
import React, { useState, useEffect, useRef } from 'react';
import { useNavigate, useSearchParams } from 'react-router-dom';
import api from '../../config/api';
import './OtpVerification.css';
import { getAuthDestination, getAuthEntry } from '../../utils/authFlow';

const OtpVerification = () => {
  const [otp, setOtp] = useState('');
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');
  const [resendTimer, setResendTimer] = useState(30);
  const [canResend, setCanResend] = useState(false);
  const navigate = useNavigate();
  const verified = useRef(false);
  
  // Retrieve URL search parameters to determine post-verification redirect
  const [searchParams] = useSearchParams();
  // "redirect-to" query param holds the path we should navigate to after OTP verification
  const pageParam = searchParams.get('redirect-to');

  const mobileNumber = localStorage.getItem('tempMobileNumber');
  const eventCode = localStorage.getItem('eventCode');

  useEffect(() => {
    if (verified.current) return;
    if (!mobileNumber) {
      navigate(getAuthEntry(eventCode, pageParam), { replace: true });
      return;
    }

    if (canResend) return;

    const timer = setInterval(() => {
      setResendTimer((prev) => {
        if (prev <= 1) {
          clearInterval(timer);
          setCanResend(true);
          return 0;
        }
        return prev - 1;
      });
    }, 1000);

    return () => clearInterval(timer);
  }, [mobileNumber, eventCode, pageParam, navigate, canResend]);

  const validateOtp = (otpValue) => {
    // OTP must be exactly 6 digits
    const otpRegex = /^[0-9]{6}$/;
    return otpRegex.test(otpValue);
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (loading || verified.current) return;
    setError('');

    if (!validateOtp(otp)) {
      setError('Please enter a valid 6-digit OTP');
      return;
    }

    setLoading(true);

    try {
      const response = await api.post('/users/verify/otp', {
        mobile_number: mobileNumber,
        otp: otp
      });

      const payload = response.data?.data ?? response.data;
      const accessToken = payload?.access_token;
      const user = payload?.user;
      const successful = response.data?.status === 'SUCCESS' || response.data?.status == null;

      if (successful) {
        if (typeof accessToken !== 'string' || !accessToken.trim() || accessToken === 'undefined' || accessToken === 'null') {
          setError('Verification did not return a valid session token. Please try again.');
          return;
        }

        // Store account completion status for route protection
        const isCompleteAccount = user?.is_complete_account === true;
        localStorage.setItem('isCompleteAccount', isCompleteAccount ? 'true' : 'false');
        if (isCompleteAccount) {
          localStorage.setItem('authToken', accessToken);
          sessionStorage.removeItem('pendingAuthToken');
          sessionStorage.removeItem('pendingMobileNumber');
        } else {
          sessionStorage.setItem('pendingAuthToken', accessToken);
          sessionStorage.setItem('pendingMobileNumber', mobileNumber);
          localStorage.removeItem('authToken');
          localStorage.setItem('tempMobileNumber', mobileNumber);
        }
        // Prevent the missing-mobile guard from overriding navigation after cleanup.
        verified.current = true;
        notifyAuthChanged();

        // Complete account setup before continuing to the profile or event.
        if (isCompleteAccount) {
          localStorage.removeItem('tempMobileNumber');
          navigate(getAuthDestination(eventCode, pageParam), { replace: true });
        } else {
          // Redirect to complete account page
          navigate(pageParam ? '/auth/complete-account?redirect-to=' + encodeURIComponent(pageParam) : '/auth/complete-account', { replace: true });
        }
      } else {
        setError('Invalid OTP. Please try again.');
      }
    } catch (err) {
      verified.current = false;
      setError(err.response?.data?.message || 'OTP verification failed. Please try again.');
    } finally {
      setLoading(false);
    }
  };

  const handleResendOtp = async () => {
    if (!canResend) return;

    setError('');
    setCanResend(false);
    setResendTimer(30);

    try {
      const response = await api.post('/users/signin', {
        mobile_number: mobileNumber
      });

      if (response.data.status === 'SUCCESS') {
        setError('OTP resent successfully');
      } else {
        setError('Failed to resend OTP');
      }
    } catch (err) {
      setError('Failed to resend OTP. Please try again.');
    }
  };

  const handleOtpChange = (e) => {
    const value = e.target.value.replace(/\D/g, '').slice(0, 6);
    setOtp(value);
  };

  return (
    <>

      <div className="otp-container">
        <div className="otp-card">
        <h2>Verify OTP</h2>
        <p>Enter the OTP sent to {mobileNumber}</p>
        
        <form onSubmit={handleSubmit} className="otp-form">
          <div className="form-group">
            <label htmlFor="otp">Enter OTP</label>
            <input
              type="text"
              inputMode="numeric"
              autoComplete="one-time-code"
              id="otp"
              value={otp}
              onChange={handleOtpChange}
              placeholder="Enter 6-digit OTP"
              maxLength="6"
              minLength="6"
              pattern="[0-9]{6}"
              required
              className="otp-input"
            />
          </div>

          {error && <div className="error-message" role="alert">{error}</div>}

          <button 
            type="submit" 
            className="submit-btn"
            disabled={loading || otp.length !== 6}
          >
            {loading ? 'Verifying...' : 'Verify OTP'}
          </button>

          <div className="resend-section">
            <span>Didn't receive OTP?</span>
            {canResend ? (
              <button 
                type="button" 
                onClick={handleResendOtp}
                className="resend-btn"
              >
                Resend OTP
              </button>
            ) : (
              <span className="timer">Resend in {resendTimer}s</span>
            )}
          </div>
        </form>
        </div>
      </div>
    </>
  );
};

export default OtpVerification;
